import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase';
import SchoolSidebar from './SchoolSidebar';
import { SchoolMobileHeader } from './SchoolMobileHeader';
import './SchoolDashboard.css';
import './AttendanceRecords.css';
import BranchSelector from './BranchSelector';
import { useBranchFilter } from '../../hooks/useBranchFilter';

function AttendanceRecords() {
  const navigate = useNavigate();
  const [schoolData, setSchoolData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { selectedBranchId, handleBranchChange } = useBranchFilter();
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [recordsLoading, setRecordsLoading] = useState(false);
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedSection, setSelectedSection] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  // New: date range + search/sort/pagination
  const [dateFrom, setDateFrom] = useState(() => new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0]);
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().split('T')[0]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortKey, setSortKey] = useState('date'); // date | student | status
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  // New: mark attendance modal
  const [isMarkModalOpen, setIsMarkModalOpen] = useState(false);
  const [markDate, setMarkDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [markStudents, setMarkStudents] = useState([]); // {id, name, present}

  useEffect(() => {
    checkAuthentication();
  }, []);

  useEffect(() => {
    if (schoolData) {
      fetchClasses();
    }
  }, [schoolData, selectedBranchId]);

  useEffect(() => {
    setSelectedClass('');
    setSelectedSection('');
    setAttendanceRecords([]);
  }, [selectedBranchId]);

  useEffect(() => {
    if (selectedClass) {
      fetchSections();
    }
  }, [selectedClass, selectedBranchId]);

  useEffect(() => {
    if (selectedClass && selectedSection && schoolData) {
      fetchAttendanceRecords();
    }
  }, [selectedClass, selectedSection, dateFrom, dateTo, selectedBranchId]);

  const getClassDisplayName = (className) => {
    // Extract only the class name part (before " - ") if section name is included
    if (className && className.includes(' - ')) {
      return className.split(' - ')[0].trim();
    }
    return className;
  };

  const checkAuthentication = async () => {
    try {
      const authenticated = localStorage.getItem('schoolAuthenticated');
      const schoolDataStr = localStorage.getItem('schoolData');
      
      if (authenticated === 'true' && schoolDataStr) {
        const data = JSON.parse(schoolDataStr);
        setSchoolData(data);
      } else {
        navigate('/schoollogin');
      }
    } catch (error) {
      console.error('Authentication check failed:', error);
      navigate('/schoollogin');
    } finally {
      setLoading(false);
    }
  };

  const fetchClasses = async () => {
    try {
      let query = supabase
        .from('classes')
        .select('*')
        .eq('school_id', schoolData.id)
        .order('class_name', { ascending: true });

      if (selectedBranchId != null) {
        query = query.eq('branch_id', selectedBranchId);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching classes:', error);
        return;
      }

      // Deduplicate classes by base class name (without section) - if multiple classes have the same base name, keep only one
      const uniqueClassesMap = new Map();
      (data || []).forEach(cls => {
        const baseClassName = getClassDisplayName(cls.class_name);
        if (!uniqueClassesMap.has(baseClassName)) {
          uniqueClassesMap.set(baseClassName, cls);
        }
      });
      
      // Convert map to array
      const uniqueClasses = Array.from(uniqueClassesMap.values());
      setClasses(uniqueClasses);
    } catch (error) {
      console.error('Error fetching classes:', error);
    }
  };

  const fetchSections = async () => {
    try {
      if (!selectedClass) {
        setSections([]);
        return;
      }

      // First, get the selected class from database to get its class_name
      let selectedClassQuery = supabase
        .from('classes')
        .select('id, class_name')
        .eq('id', selectedClass)
        .eq('school_id', schoolData.id);

      if (selectedBranchId != null) {
        selectedClassQuery = selectedClassQuery.eq('branch_id', selectedBranchId);
      }

      const { data: selectedClassData, error: selectedClassError } = await selectedClassQuery.maybeSingle();

      if (selectedClassError || !selectedClassData) {
        console.error('Error fetching selected class:', selectedClassError);
        setSections([]);
        return;
      }

      const baseClassName = getClassDisplayName(selectedClassData.class_name);
      console.log('Fetching sections for base class:', baseClassName, 'from selected class:', selectedClassData.class_name);
      
      // Find all classes where the base class name matches (to get all sections from all classes with same base name)
      let classesQuery = supabase
        .from('classes')
        .select('id, class_name, sections')
        .eq('school_id', schoolData.id);

      if (selectedBranchId != null) {
        classesQuery = classesQuery.eq('branch_id', selectedBranchId);
      }

      const { data: allClassesData, error: classesError } = await classesQuery;

      if (classesError) {
        console.error('Error fetching classes for sections:', classesError);
        setSections([]);
        return;
      }

      console.log('All classes data:', allClassesData);

      // Filter classes where the base class name matches the selected one
      const matchingClasses = (allClassesData || []).filter(cls => {
        const clsBaseName = getClassDisplayName(cls.class_name);
        return clsBaseName === baseClassName;
      });

      console.log('Matching classes:', matchingClasses);

      if (matchingClasses.length === 0) {
        console.log('No matching classes found');
        setSections([]);
        return;
      }

      const classIds = matchingClasses.map(cls => cls.id);
      console.log('Class IDs:', classIds);

      // Collect all sections from multiple sources
      const allSections = new Set();

      // 1. Extract section names from class_name (PRIMARY SOURCE - e.g., "Dolphins" from "Grade 1 - Dolphins")
      matchingClasses.forEach(cls => {
        if (cls.class_name && cls.class_name.includes(' - ')) {
          const sectionName = cls.class_name.split(' - ')[1]?.trim();
          if (sectionName) {
            console.log('Extracted section from class_name:', sectionName);
            allSections.add(sectionName);
          }
        }
      });

      // 2. Get sections from classes.sections array field (handle string or array)
      matchingClasses.forEach(cls => {
        if (cls.sections) {
          let sectionsArray = cls.sections;
          
          // Handle if sections is stored as a string (JSON string)
          if (typeof sectionsArray === 'string' && sectionsArray.trim() !== '') {
            try {
              const parsed = JSON.parse(sectionsArray);
              if (Array.isArray(parsed)) {
                sectionsArray = parsed;
              }
            } catch (e) {
              // If not JSON, treat as single section
              sectionsArray = [sectionsArray];
            }
          }
          
          if (Array.isArray(sectionsArray)) {
            sectionsArray.forEach(section => {
              if (section && String(section).trim() !== '') {
                console.log('Added section from sections array:', section);
                allSections.add(String(section).trim());
              }
            });
          }
        }
      });

      // 3. Get sections from students table for these classes
      let studentsQuery = supabase
        .from('students')
        .select('section_id')
        .eq('school_id', schoolData.id)
        .in('class_id', classIds)
        .not('section_id', 'is', null);

      if (selectedBranchId != null) {
        studentsQuery = studentsQuery.eq('branch_id', selectedBranchId);
      }

      const { data: studentsData, error: studentsError } = await studentsQuery;
      console.log('Students data:', studentsData, 'Error:', studentsError);
      if (!studentsError && studentsData) {
        studentsData.forEach(student => {
          if (student.section_id && String(student.section_id).trim() !== '') {
            console.log('Added section from students:', student.section_id);
            allSections.add(String(student.section_id).trim());
          }
        });
      }

      // 4. Get sections from attendance records for these classes
      let attendanceQuery = supabase
        .from('attendance')
        .select('section_id')
        .eq('school_id', schoolData.id)
        .in('class_id', classIds)
        .not('section_id', 'is', null);

      if (selectedBranchId != null) {
        attendanceQuery = attendanceQuery.eq('branch_id', selectedBranchId);
      }

      const { data: attendanceData, error: attendanceError } = await attendanceQuery;
      console.log('Attendance data:', attendanceData, 'Error:', attendanceError);
      if (!attendanceError && attendanceData) {
        attendanceData.forEach(record => {
          if (record.section_id && String(record.section_id).trim() !== '') {
            console.log('Added section from attendance:', record.section_id);
            allSections.add(String(record.section_id).trim());
          }
        });
      }

      // Convert to sorted array
      const uniqueSections = Array.from(allSections).sort();
      console.log('Final sections list:', uniqueSections);
      setSections(uniqueSections);
    } catch (error) {
      console.error('Error fetching sections:', error);
      setSections([]);
    }
  };

  const fetchAttendanceRecords = async () => {
    try {
      setRecordsLoading(true);
      const startDate = dateFrom;
      const endDate = dateTo;

      // Get the selected class from database to get its class_name
      let selectedClassQuery = supabase
        .from('classes')
        .select('id, class_name')
        .eq('id', selectedClass)
        .eq('school_id', schoolData.id);

      if (selectedBranchId != null) {
        selectedClassQuery = selectedClassQuery.eq('branch_id', selectedBranchId);
      }

      const { data: selectedClassData, error: selectedClassError } = await selectedClassQuery.maybeSingle();

      if (selectedClassError || !selectedClassData) {
        console.error('Error fetching selected class:', selectedClassError);
        setAttendanceRecords([]);
        setRecordsLoading(false);
        return;
      }

      const baseClassName = getClassDisplayName(selectedClassData.class_name);

      // Find all class IDs with the same base class name
      let classesQuery = supabase
        .from('classes')
        .select('id, class_name')
        .eq('school_id', schoolData.id);

      if (selectedBranchId != null) {
        classesQuery = classesQuery.eq('branch_id', selectedBranchId);
      }

      const { data: allClassesData, error: classesError } = await classesQuery;

      if (classesError || !allClassesData || allClassesData.length === 0) {
        console.error('Error fetching classes for attendance:', classesError);
        setAttendanceRecords([]);
        setRecordsLoading(false);
        return;
      }

      // Filter classes where the base class name matches
      const matchingClasses = allClassesData.filter(cls => {
        const clsBaseName = getClassDisplayName(cls.class_name);
        return clsBaseName === baseClassName;
      });

      if (matchingClasses.length === 0) {
        setAttendanceRecords([]);
        setRecordsLoading(false);
        return;
      }

      const classIds = matchingClasses.map(cls => cls.id);

      console.log('Fetching attendance records for:', {
        school_id: schoolData.id,
        class_name: baseClassName,
        class_ids: classIds,
        section_id: selectedSection,
        startDate,
        endDate
      });

      // Fetch attendance records for all classes with the same name
      let recordsQuery = supabase
        .from('attendance')
        .select('*')
        .eq('school_id', schoolData.id)
        .in('class_id', classIds)
        .eq('section_id', selectedSection)
        .gte('attendance_date', startDate)
        .lte('attendance_date', endDate)
        .order('attendance_date', { ascending: false });

      if (selectedBranchId != null) {
        recordsQuery = recordsQuery.eq('branch_id', selectedBranchId);
      }

      const { data: records, error: recordsError } = await recordsQuery;

      if (recordsError) {
        console.error('Error fetching attendance records:', recordsError);
        return;
      }

      console.log('Attendance records fetched:', records);

      // Fetch student data
      const studentIds = [...new Set(records.map(record => record.student_id))];
      let studentsQuery = supabase
        .from('students')
        .select('*')
        .eq('school_id', schoolData.id)
        .in('class_id', classIds)
        .eq('section_id', selectedSection);

      if (selectedBranchId != null) {
        studentsQuery = studentsQuery.eq('branch_id', selectedBranchId);
      }

      const { data: students, error: studentsError } = await studentsQuery;

      if (studentsError) {
        console.error('Error fetching students:', studentsError);
        return;
      }

      // Fetch teacher data
      const teacherIds = [...new Set(records.map(record => record.teacher_id))];
      let teachersQuery = supabase
        .from('teachers')
        .select('*')
        .eq('school_id', schoolData.id)
        .in('id', teacherIds);

      if (selectedBranchId != null) {
        teachersQuery = teachersQuery.eq('branch_id', selectedBranchId);
      }

      const { data: teachers, error: teachersError } = await teachersQuery;

      if (teachersError) {
        console.error('Error fetching teachers:', teachersError);
        return;
      }

      // Create maps for efficient lookup
      const studentsMap = {};
      students?.forEach(student => {
        if (student.id) studentsMap[student.id] = student;
        if (student.student_id) studentsMap[student.student_id] = student;
      });

      const teachersMap = {};
      teachers?.forEach(teacher => {
        teachersMap[teacher.id] = teacher;
      });

      // Combine data
      const enrichedRecords = records.map(record => ({
        ...record,
        student: studentsMap[record.student_id] || studentsMap[parseInt(record.student_id)],
        teacher: teachersMap[record.teacher_id]
      }));

      console.log('Enriched records:', enrichedRecords);
      setAttendanceRecords(enrichedRecords);

    } catch (error) {
      console.error('Error fetching attendance records:', error);
    } finally {
      setRecordsLoading(false);
    }
  };

  const getMonthName = (monthIndex) => {
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return months[monthIndex];
  };

  const navigateMonth = (direction) => {
    if (direction === 'prev') {
      if (selectedMonth === 0) {
        setSelectedMonth(11);
        setSelectedYear(selectedYear - 1);
      } else {
        setSelectedMonth(selectedMonth - 1);
      }
    } else {
      if (selectedMonth === 11) {
        setSelectedMonth(0);
        setSelectedYear(selectedYear + 1);
      } else {
        setSelectedMonth(selectedMonth + 1);
      }
    }
  };

  const groupRecordsByDate = () => {
    // Filter by search and sort flat, then group
    let rows = [...attendanceRecords];
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      rows = rows.filter(r => {
        const name = r.student ? `${r.student.first_name || ''} ${r.student.last_name || ''}`.toLowerCase() : '';
        return name.includes(term) || String(r.student_id).includes(term) || (r.teacher ? `${r.teacher.first_name || ''} ${r.teacher.last_name || ''}`.toLowerCase().includes(term) : false);
      });
    }
    rows.sort((a,b)=>{
      let va, vb;
      switch (sortKey) {
        case 'student': va = a.student ? `${a.student.first_name||''} ${a.student.last_name||''}` : ''; vb = b.student ? `${b.student.first_name||''} ${b.student.last_name||''}` : ''; break;
        case 'status': va = a.status; vb = b.status; break;
        default: va = new Date(a.attendance_date).getTime(); vb = new Date(b.attendance_date).getTime();
      }
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    // Group
    const grouped = {};
    rows.forEach(record => {
      const date = record.attendance_date;
      if (!grouped[date]) grouped[date] = [];
      grouped[date].push(record);
    });
    return grouped;
  };

  const getPagedDates = (grouped) => {
    const dates = Object.keys(grouped).sort((a,b)=> sortDir==='asc' ? new Date(a)-new Date(b) : new Date(b)-new Date(a));
    const totalPages = Math.max(1, Math.ceil(dates.length / pageSize));
    const currentPage = Math.min(page, totalPages);
    const startIdx = (currentPage - 1) * pageSize;
    return { dates: dates.slice(startIdx, startIdx + pageSize), totalPages, currentPage };
  };

  const openMarkModal = async () => {
    if (!selectedClass || !selectedSection) return;
    try {
      // Get the selected class from database to get its class_name
      let selectedClassQuery = supabase
        .from('classes')
        .select('id, class_name')
        .eq('id', selectedClass)
        .eq('school_id', schoolData.id);

      if (selectedBranchId != null) {
        selectedClassQuery = selectedClassQuery.eq('branch_id', selectedBranchId);
      }

      const { data: selectedClassData, error: selectedClassError } = await selectedClassQuery.maybeSingle();

      if (selectedClassError || !selectedClassData) {
        console.error('Error fetching selected class:', selectedClassError);
        return;
      }

      const baseClassName = getClassDisplayName(selectedClassData.class_name);

      // Find all class IDs with the same base class name
      let classesQuery = supabase
        .from('classes')
        .select('id, class_name')
        .eq('school_id', schoolData.id);

      if (selectedBranchId != null) {
        classesQuery = classesQuery.eq('branch_id', selectedBranchId);
      }

      const { data: allClassesData, error: classesError } = await classesQuery;

      if (classesError || !allClassesData || allClassesData.length === 0) {
        return;
      }

      // Filter classes where the base class name matches
      const matchingClasses = allClassesData.filter(cls => {
        const clsBaseName = getClassDisplayName(cls.class_name);
        return clsBaseName === baseClassName;
      });

      if (matchingClasses.length === 0) {
        return;
      }

      const classIds = matchingClasses.map(cls => cls.id);

      // Load students of all classes with same name and the selected section
      const { data: students, error } = await supabase
        .from('students')
        .select('id, first_name, last_name')
        .eq('school_id', schoolData.id)
        .in('class_id', classIds)
        .eq('section_id', selectedSection)
        .eq('is_active', true);
      if (!error) {
        setMarkStudents((students||[]).map(s => ({ id: s.id, name: `${s.first_name||''} ${s.last_name||''}`.trim() || `Student ${s.id}` , present: true })));
        setIsMarkModalOpen(true);
      }
    } catch {}
  };

  const toggleStudentPresent = (id) => {
    setMarkStudents(prev => prev.map(s => s.id === id ? { ...s, present: !s.present } : s));
  };

  const setAllPresent = (val) => {
    setMarkStudents(prev => prev.map(s => ({ ...s, present: val })));
  };

  const saveMarkedAttendance = async () => {
    try {
      // Get the selected class from database to get its class_name
      let selectedClassQuery = supabase
        .from('classes')
        .select('id, class_name')
        .eq('id', selectedClass)
        .eq('school_id', schoolData.id);

      if (selectedBranchId != null) {
        selectedClassQuery = selectedClassQuery.eq('branch_id', selectedBranchId);
      }

      const { data: selectedClassData, error: selectedClassError } = await selectedClassQuery.maybeSingle();

      if (selectedClassError || !selectedClassData) {
        alert('Error: Class not found');
        return;
      }

      const baseClassName = getClassDisplayName(selectedClassData.class_name);

      // Find all class IDs with the same base class name
      let classesQuery = supabase
        .from('classes')
        .select('id, class_name')
        .eq('school_id', schoolData.id);

      if (selectedBranchId != null) {
        classesQuery = classesQuery.eq('branch_id', selectedBranchId);
      }

      const { data: allClassesData, error: classesError } = await classesQuery;

      if (classesError || !allClassesData || allClassesData.length === 0) {
        alert('Error: Classes not found');
        return;
      }

      // Filter classes where the base class name matches
      const matchingClasses = allClassesData.filter(cls => {
        const clsBaseName = getClassDisplayName(cls.class_name);
        return clsBaseName === baseClassName;
      });

      if (matchingClasses.length === 0) {
        alert('Error: No matching classes found');
        return;
      }

      const classIds = matchingClasses.map(cls => cls.id);

      // Delete existing for date/class/section to allow re-marking (for all classes with same name)
      let deleteQuery = supabase
        .from('attendance')
        .delete()
        .eq('school_id', schoolData.id)
        .in('class_id', classIds)
        .eq('section_id', selectedSection)
        .eq('attendance_date', markDate);

      if (selectedBranchId != null) {
        deleteQuery = deleteQuery.eq('branch_id', selectedBranchId);
      }

      await deleteQuery;

      // Get student class mappings
      const { data: studentsData } = await supabase
        .from('students')
        .select('id, class_id')
        .eq('school_id', schoolData.id)
        .in('id', markStudents.map(s => s.id))
        .in('class_id', classIds)
        .eq('section_id', selectedSection);

      const studentClassMap = {};
      studentsData?.forEach(s => {
        studentClassMap[s.id] = s.class_id;
      });

      const entries = markStudents.map(s => ({
        school_id: schoolData.id,
        class_id: studentClassMap[s.id] || selectedClass, // Use student's actual class_id
        section_id: selectedSection,
        student_id: s.id,
        status: s.present ? 'present' : 'absent',
        attendance_date: markDate,
        branch_id: selectedBranchId,
        created_at: new Date().toISOString()
      }));
      if (entries.length > 0) {
        const { error } = await supabase.from('attendance').insert(entries);
        if (error) {
          alert('Failed to save attendance: ' + error.message);
          return;
        }
      }
      setIsMarkModalOpen(false);
      await fetchAttendanceRecords();
      alert('Attendance saved for ' + markDate);
    } catch (err) {
      alert('Error saving attendance');
    }
  };

  const getAttendanceStats = () => {
    const totalRecords = attendanceRecords.length;
    const presentCount = attendanceRecords.filter(record => record.status === 'present').length;
    const absentCount = attendanceRecords.filter(record => record.status === 'absent').length;
    const uniqueStudents = [...new Set(attendanceRecords.map(record => record.student_id))].length;
    const uniqueTeachers = [...new Set(attendanceRecords.map(record => record.teacher_id))].length;

    return {
      totalRecords,
      presentCount,
      absentCount,
      uniqueStudents,
      uniqueTeachers
    };
  };

  const handleLogout = () => {
    navigate('/schoollogin');
  };

  if (loading) {
    return (
      <div className="school-dashboard">
        <div className="loading-container">
          <div className="loading-spinner">⏳</div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (!schoolData) {
    return (
      <div className="school-dashboard">
        <div className="error-container">
          <h2>Access Denied</h2>
          <p>Please log in to access the school dashboard.</p>
          <button onClick={() => navigate('/schoollogin')} className="btn-primary">
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  const stats = getAttendanceStats();
  const groupedRecords = groupRecordsByDate();

  return (
    <div className="school-dashboard">
      <SchoolMobileHeader 
        schoolData={schoolData} 
        mobileMenuOpen={mobileMenuOpen} 
        setMobileMenuOpen={setMobileMenuOpen} 
        handleLogout={handleLogout} 
      />
      
      {/* Sidebar */}
      <SchoolSidebar schoolData={schoolData} onLogout={handleLogout} />
      
      <div className="main-content">
        <div className="attendance-records-container">
          <div className="records-header-card">
            <div className="records-header-title">
              <h1 className="records-title">Attendance Records</h1>
            </div>
            <p className="records-subtitle">View all attendance records for your school</p>
          </div>
          <div className="branch-filter-row">
            <BranchSelector
              schoolId={schoolData?.id}
              selectedBranchId={selectedBranchId}
              onBranchChange={handleBranchChange}
            />
            <span className="branch-context-text">
              {selectedBranchId
                ? 'Filtering attendance records for the selected branch'
                : 'Showing attendance across all branches'}
            </span>
          </div>

          {/* Filters */}
          <div className="records-filters">
            <div className="filter-group">
              <label htmlFor="class-select">Class:</label>
              <select 
                id="class-select"
                value={selectedClass} 
                onChange={(e) => {
                  setSelectedClass(e.target.value);
                  setSelectedSection('');
                }}
                className="filter-select"
              >
                <option value="">Select Class</option>
                {classes.map(cls => (
                  <option key={cls.id} value={cls.id}>
                    {getClassDisplayName(cls.class_name)}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label htmlFor="section-select">Section:</label>
              <select 
                id="section-select"
                value={selectedSection} 
                onChange={(e) => setSelectedSection(e.target.value)}
                className="filter-select"
                disabled={!selectedClass}
              >
                <option value="">Select Section</option>
                {sections.map(section => (
                  <option key={section} value={section}>
                    {section}
                  </option>
                ))}
              </select>
            </div>

            <div className="date-range">
              <div>
              <label>From:</label>
              <input type="date" value={dateFrom} onChange={(e)=>{ setDateFrom(e.target.value); setPage(1); }} />
              </div>
              <div>
              <label>To:</label>
              <input type="date" value={dateTo} onChange={(e)=>{ setDateTo(e.target.value); setPage(1); }} />
            </div>
            </div>
            <div className="mark-actions">
              <button className="btn-primary" disabled={!selectedClass || !selectedSection} onClick={openMarkModal}>View Attendance</button>
            </div>
          </div>

          {/* Statistics */}
          {attendanceRecords.length > 0 && (
            <div className="attendance-stats-grid">
              <div className="stat-card stat-card-orange">
                <div className="stat-number">{stats.totalRecords}</div>
                <div className="stat-label">Total Records</div>
              </div>
              <div className="stat-card stat-card-green">
                <div className="stat-number">{stats.presentCount}</div>
                <div className="stat-label">Present</div>
              </div>
              <div className="stat-card stat-card-red">
                <div className="stat-number">{stats.absentCount}</div>
                <div className="stat-label">Absent</div>
              </div>
              <div className="stat-card stat-card-purple">
                <div className="stat-number">
                  {stats.totalRecords > 0 
                    ? ((stats.presentCount / stats.totalRecords) * 100).toFixed(1) 
                    : 0}%
              </div>
                <div className="stat-label">Attendance %</div>
              </div>
            </div>
          )}

          {/* Records Display */}
          {recordsLoading ? (
            <div className="loading-container">
              <div className="loading-spinner">⏳</div>
              <p>Loading attendance records...</p>
            </div>
          ) : !selectedClass || !selectedSection ? (
            <div className="select-prompt">
              <div className="select-prompt-icon">📋</div>
              <h3>Select Class and Section</h3>
              <p>Please select a class and section to view attendance records</p>
            </div>
          ) : attendanceRecords.length === 0 ? (
            <div className="no-records">
              <div className="no-records-icon">
                <svg width="80" height="80" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="3" y="4" width="18" height="18" rx="2" stroke="#D1D5DB" strokeWidth="2"/>
                  <path d="M3 10h18M8 4v6" stroke="#D1D5DB" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
              <h3>No Attendance Records</h3>
              <p>No attendance records found for this date range</p>
              <p>Records will appear here once teachers start marking attendance</p>
            </div>
          ) : (
            <div className="attendance-list-container">
              {(() => { const pg = getPagedDates(groupedRecords); return pg.dates.map((date, dateIndex) => { const records = groupedRecords[date]; return (
                <div key={date} className="date-group-wrapper">
                  <div className="date-group-header">
                    <span className="date-text">
                      {new Date(date).toLocaleDateString('en-US', { 
                      weekday: 'long', 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                      })}
                    </span>
                    <span className="date-count-badge">{records.length} {records.length === 1 ? 'record' : 'records'}</span>
                  </div>
                  <div className="students-grid">
                    {records.map(record => (
                      <div key={`${record.student_id}-${record.attendance_date}`} className="student-card">
                        <div className="student-name">
                            {record.student?.first_name && record.student?.last_name 
                              ? `${record.student.first_name} ${record.student.last_name}`
                              : `Student ${record.student_id}`
                            }
                        </div>
                        <div className="teacher-name">
                            {record.teacher?.first_name && record.teacher?.last_name 
                              ? `${record.teacher.first_name} ${record.teacher.last_name}`
                              : `Teacher ${record.teacher_id}`
                            }
                        </div>
                        <div className="student-id-badge">
                          ID: {record.student?.student_id || record.student_id}
                        </div>
                        <div className={`status-badge ${record.status}`}>
                          {record.status === 'present' ? (
                            <>
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                              Present
                            </>
                          ) : (
                            <>
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                              Absent
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ); })})()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AttendanceRecords;
